"use client";
import Link from "next/link";

import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { routes } from "@/lib/routes";

interface FooterLink {
  href: string;
  label: string;
}

export default function PublicFooter() {
  const currentYear = new Date().getFullYear();

  // Only include links for pages that exist
  const footerLinks: Record<string, FooterLink[]> = {
    "Quick Links": [
      { href: routes.public.about(), label: "About Us" },
      { href: routes.public.howItWorks(), label: "How It Works" },
      { href: routes.public.features(), label: "Features" },
      { href: routes.public.pricing(), label: "Pricing" },
    ],

    // Support intake is the contact form and the docs, both already linked here.
    Resources: [
      { href: routes.public.docsHome(), label: "Documentation" },
      { href: routes.public.useCases(), label: "Use Cases" },
      { href: routes.public.contact(), label: "Contact" },
    ],
  } as const;

  return (
    <footer
      className="w-full py-8 bg-gray-50 print:hidden"
      data-testid="public-footer"
      role="contentinfo"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              NeedBridge
            </h3>
            <p className="text-gray-600 mb-4 max-w-md">
              Connecting families in crisis with community volunteers through
              simple, automated coordination tools.
            </p>
            <div className="flex space-x-4">
              <Link
                className="text-blue-600 hover:text-blue-800"
                href={routes.auth.register()}
                onClick={() => {
                  void EventPublisher.publishEvent(
                    EventKeys.PUBLIC_FOOTER_LINK_CLICK,
                    {
                      href: "/register",
                      label: "Get Started",
                      section: "Company Info",
                    },
                  );
                }}
              >
                Get Started
              </Link>
              <Link
                className="text-blue-600 hover:text-blue-800"
                href={routes.public.contact()}
                onClick={() => {
                  void EventPublisher.publishEvent(
                    EventKeys.PUBLIC_FOOTER_LINK_CLICK,
                    {
                      href: "/contact",
                      label: "Contact Us",
                      section: "Company Info",
                    },
                  );
                }}
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([section, links]) => (
            <div key={section}>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">
                {section}
              </h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                      href={link.href}
                      onClick={() => {
                        void EventPublisher.publishEvent(
                          EventKeys.PUBLIC_FOOTER_LINK_CLICK,
                          {
                            href: link.href,
                            label: link.label,
                            section,
                          },
                        );
                      }}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-600">
              © {currentYear} NeedBridge. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
